import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, ArrowUp, ArrowDown, Copy, Trash2, Plus, Eye, Save, GripVertical } from "lucide-react";
import { RelationRenderer } from "@/components/office/RelationRenderer";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `f-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const FIELD_TYPES = [
  { type: "section", label: "Titolo / sezione", input: false },
  { type: "info", label: "Testo informativo", input: false },
  { type: "short_text", label: "Risposta breve", input: true },
  { type: "long_text", label: "Risposta aperta", input: true },
  { type: "semi_open", label: "Risposta semi-aperta", input: true, options: true },
  { type: "single_choice", label: "Scelta singola", input: true, options: true },
  { type: "multi_choice", label: "Scelta multipla", input: true, options: true },
  { type: "yes_no", label: "Sì / No", input: true },
  { type: "rating", label: "Scala di valutazione", input: true },
  { type: "date", label: "Data", input: true },
  { type: "number", label: "Numero", input: true },
];
const TYPE_META = Object.fromEntries(FIELD_TYPES.map((t) => [t.type, t]));

function newField(type) {
  const meta = TYPE_META[type];
  const f = { field_id: uid(), type, label: "", help_text: "", required: false };
  if (meta.options) f.options = ["Opzione 1"];
  if (type === "semi_open") f.allow_other = true;
  if (type === "rating") { f.rating_min = 1; f.rating_max = 5; f.rating_min_label = ""; f.rating_max_label = ""; }
  return f;
}

function labelPlaceholder(type) {
  if (type === "section") return "Titolo della sezione";
  if (type === "info") return "Testo o istruzione per il lavoratore";
  return "Domanda / etichetta";
}

function FieldEditor({ f, index, total, onChange, onMove, onDuplicate, onDelete }) {
  const meta = TYPE_META[f.type];
  const upd = (patch) => onChange({ ...f, ...patch });
  const setOption = (i, v) => { const o = [...(f.options || [])]; o[i] = v; upd({ options: o }); };
  const addOption = () => upd({ options: [...(f.options || []), `Opzione ${(f.options || []).length + 1}`] });
  const delOption = (i) => upd({ options: (f.options || []).filter((_, j) => j !== i) });

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid={`builder-field-${f.field_id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#5B4A7D] uppercase tracking-wide">
          <GripVertical className="h-4 w-4 text-slate-300" /> {meta.label}
        </div>
        <div className="flex items-center gap-1">
          <button title="Sposta su" disabled={index === 0} onClick={() => onMove(index, -1)}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`builder-up-${f.field_id}`}><ArrowUp className="h-4 w-4" /></button>
          <button title="Sposta giù" disabled={index === total - 1} onClick={() => onMove(index, 1)}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`builder-down-${f.field_id}`}><ArrowDown className="h-4 w-4" /></button>
          <button title="Duplica" onClick={() => onDuplicate(index)}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`builder-dup-${f.field_id}`}><Copy className="h-4 w-4" /></button>
          <button title="Elimina" onClick={() => onDelete(index)}
            className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`builder-del-${f.field_id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-500">{f.type === "info" ? "Testo" : "Etichetta"}</Label>
          {f.type === "info" ? (
            <Textarea value={f.label} onChange={(e) => upd({ label: e.target.value })} placeholder={labelPlaceholder(f.type)} rows={2} data-testid={`builder-label-${f.field_id}`} />
          ) : (
            <Input value={f.label} onChange={(e) => upd({ label: e.target.value })} placeholder={labelPlaceholder(f.type)} data-testid={`builder-label-${f.field_id}`} />
          )}
        </div>

        {meta.input && (
          <div>
            <Label className="text-xs text-slate-500">Testo di aiuto (facoltativo)</Label>
            <Input value={f.help_text} onChange={(e) => upd({ help_text: e.target.value })} placeholder="Istruzione o esempio" data-testid={`builder-help-${f.field_id}`} />
          </div>
        )}

        {meta.options && (
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Opzioni</Label>
            <div className="space-y-2">
              {(f.options || []).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Opzione ${i + 1}`} data-testid={`builder-opt-${f.field_id}-${i}`} />
                  <button onClick={() => delOption(i)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 disabled:opacity-30" disabled={(f.options || []).length <= 1} data-testid={`builder-opt-del-${f.field_id}-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addOption} className="mt-2" data-testid={`builder-opt-add-${f.field_id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi opzione</Button>
            {f.type === "semi_open" && (
              <div className="flex items-center gap-2 mt-3">
                <Switch checked={f.allow_other !== false} onCheckedChange={(v) => upd({ allow_other: v })} data-testid={`builder-other-${f.field_id}`} />
                <span className="text-sm text-slate-600">Consenti «Altro / specificare»</span>
              </div>
            )}
          </div>
        )}

        {f.type === "rating" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Da</Label>
              <Input type="number" value={f.rating_min} onChange={(e) => upd({ rating_min: parseInt(e.target.value || "1", 10) })} data-testid={`builder-rmin-${f.field_id}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">A</Label>
              <Input type="number" value={f.rating_max} onChange={(e) => upd({ rating_max: parseInt(e.target.value || "5", 10) })} data-testid={`builder-rmax-${f.field_id}`} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Etichetta minimo</Label>
              <Input value={f.rating_min_label} onChange={(e) => upd({ rating_min_label: e.target.value })} placeholder="es. mai" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Etichetta massimo</Label>
              <Input value={f.rating_max_label} onChange={(e) => upd({ rating_max_label: e.target.value })} placeholder="es. sempre" />
            </div>
          </div>
        )}

        {meta.input && (
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={!!f.required} onCheckedChange={(v) => upd({ required: v })} data-testid={`builder-req-${f.field_id}`} />
            <span className="text-sm text-slate-600">Obbligatorio</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function RelationBuilder({ templateId, onClose, onSaved }) {
  const [appalti, setAppalti] = useState([]);
  const [services, setServices] = useState([]);
  const [meta, setMeta] = useState({ title: "", description: "", appalto_id: "", service: "", period_ref: "", due_date: "" });
  const [fields, setFields] = useState([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!templateId);

  useEffect(() => {
    api.get("/office/relations/appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([]));
  }, []);
  useEffect(() => {
    if (!meta.appalto_id) { setServices([]); return; }
    api.get(`/office/relations/services?appalto_id=${meta.appalto_id}`).then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, [meta.appalto_id]);

  useEffect(() => {
    if (!templateId) return;
    api.get(`/office/relations/templates/${templateId}`).then((r) => {
      const d = r.data;
      setMeta({ title: d.title || "", description: d.description || "", appalto_id: d.appalto_id || "",
        service: d.service || "", period_ref: d.period_ref || "", due_date: d.due_date || "" });
      setFields((d.fields || []).map((f) => ({ ...f })));
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [templateId]);

  const setM = (patch) => setMeta((m) => ({ ...m, ...patch }));
  const addField = (type) => setFields((arr) => [...arr, newField(type)]);
  const updField = (i, nf) => setFields((arr) => arr.map((f, j) => (j === i ? nf : f)));
  const moveField = (i, dir) => setFields((arr) => {
    const j = i + dir; if (j < 0 || j >= arr.length) return arr;
    const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const dupField = (i) => setFields((arr) => {
    const copy = { ...arr[i], field_id: uid(), options: arr[i].options ? [...arr[i].options] : undefined };
    const c = [...arr]; c.splice(i + 1, 0, copy); return c;
  });
  const delField = (i) => setFields((arr) => arr.filter((_, j) => j !== i));

  const save = async () => {
    if (!meta.title.trim()) return toast.error("Il titolo è obbligatorio");
    if (!meta.appalto_id) return toast.error("Seleziona un appalto");
    if (!meta.service) return toast.error("Seleziona un servizio");
    if (!meta.due_date) return toast.error("La data di scadenza è obbligatoria");
    setSaving(true);
    try {
      const payload = { ...meta, fields };
      if (templateId) await api.put(`/office/relations/templates/${templateId}`, payload);
      else await api.post("/office/relations/templates", payload);
      toast.success("Bozza salvata");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-slate-500 p-4">Caricamento…</p>;

  return (
    <div className="space-y-5" data-testid="relation-builder">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="builder-back">
          <ArrowLeft className="h-4 w-4" /> Torna all'elenco
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="builder-preview-btn"><Eye className="h-4 w-4 mr-1.5" /> Anteprima</Button>
          <Button onClick={save} disabled={saving} className="bg-[#6D5FB8] hover:bg-[#5B4A9E]" data-testid="builder-save-btn"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvataggio…" : "Salva bozza"}</Button>
        </div>
      </div>

      {/* Dati generali */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 space-y-3" data-testid="builder-meta">
        <div>
          <Label className="text-xs text-slate-500">Titolo della relazione *</Label>
          <Input value={meta.title} onChange={(e) => setM({ title: e.target.value })} placeholder="Es. Relazione educativa individuale" data-testid="builder-title" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Descrizione / istruzioni</Label>
          <Textarea value={meta.description} onChange={(e) => setM({ description: e.target.value })} placeholder="Istruzioni facoltative per il compilatore" rows={2} data-testid="builder-description" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Appalto *</Label>
            <Select value={meta.appalto_id} onValueChange={(v) => setM({ appalto_id: v, service: "" })}>
              <SelectTrigger data-testid="builder-appalto"><SelectValue placeholder="Seleziona appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Servizio *</Label>
            <Select value={meta.service} onValueChange={(v) => setM({ service: v })} disabled={!meta.appalto_id}>
              <SelectTrigger data-testid="builder-service"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Periodo di riferimento</Label>
            <Input value={meta.period_ref} onChange={(e) => setM({ period_ref: e.target.value })} placeholder="Es. I quadrimestre 2025/2026" data-testid="builder-period" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Data di scadenza *</Label>
            <Input type="date" value={meta.due_date} onChange={(e) => setM({ due_date: e.target.value })} data-testid="builder-due" />
          </div>
        </div>
      </div>

      {/* Builder campi */}
      <div className="space-y-3">
        {fields.map((f, i) => (
          <FieldEditor key={f.field_id} f={f} index={i} total={fields.length}
            onChange={(nf) => updField(i, nf)} onMove={moveField} onDuplicate={dupField} onDelete={delField} />
        ))}
        {fields.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="builder-empty">
            <p className="text-sm text-slate-500">Nessun campo. Inizia aggiungendo il primo elemento della relazione.</p>
          </div>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full border-dashed border-[#8478C4] text-[#5B4A7D]" data-testid="builder-add-field"><Plus className="h-4 w-4 mr-1.5" /> Aggiungi campo</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64" align="start">
          {FIELD_TYPES.map((t) => (
            <DropdownMenuItem key={t.type} onClick={() => addField(t.type)} data-testid={`builder-addtype-${t.type}`}>{t.label}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="builder-preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima relazione</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <RelationRenderer title={meta.title} description={meta.description} fields={fields} readOnly />
          </div>
          <p className="text-xs text-slate-400 text-center">Anteprima visuale · non salva risposte</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RelationBuilder;
